import Seo from '@/components/public/Seo';
import PageHero from '@/components/public/sections/PageHero';
import { parishName, useSite } from '@/hooks/useSite';
import { trackUmamiEvent } from '@/lib/analytics';

const sectionLabel =
  'text-xs font-bold tracking-widest uppercase text-primary \
  mb-3 block';

// Fixed accessibility declaration. The legal text is the same for every
// parish; only the parish-specific references (name, seat/address, contacts)
// react to the configured parish via useSite().
export default function Accessibility() {
  const site = useSite();
  const name = parishName(site);
  const hq = site.locations.find((l) => l.headquarters) ?? site.locations[0];
  const baseClassName = 'max-w-7xl mx-auto px-4 md:px-8 py-16';

  return (
    <>
      <Seo
        title="Acessibilidade"
        description={`Declaração de acessibilidade de ${name}, nos termos do Decreto-Lei n.º 83/2018.`}
      />
      <PageHero
        badge="Acessibilidade"
        breadcrumbs={[{ label: 'Início', href: '/' }, { label: 'Declaração de Acessibilidade' }]}
        title="Declaração de Acessibilidade."
        accentWord="Acessibilidade."
        subtitle={`Compromisso de ${name} com a acessibilidade digital, nos termos do Decreto-Lei n.º 83/2018.`}
      />
      <div className={baseClassName}>
        <div className="mx-auto max-w-4xl">
          <article className="surface-card text-on-surface-light space-y-8 p-6 leading-relaxed md:p-10">
            <section>
              <span className={sectionLabel}>1 — Compromisso</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                O nosso compromisso
              </h2>
              <p>
                {name} está empenhada em tornar este sítio Web acessível a todas as pessoas, em
                conformidade com o Decreto-Lei n.º 83/2018, de 19 de outubro, que transpõe a
                Diretiva (UE) 2016/2102 relativa à acessibilidade dos sítios web e das aplicações
                móveis dos organismos do setor público.
              </p>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>2 — Estado de conformidade</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Estado de conformidade
              </h2>
              <p>
                Este sítio Web encontra-se em{' '}
                <strong className="text-on-surface font-semibold">conformidade parcial</strong> com
                as Diretrizes de Acessibilidade para Conteúdo Web (WCAG) 2.1, nível AA, devido às
                não conformidades e exceções identificadas na secção seguinte.
              </p>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>3 — Conteúdo não acessível</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Limitações conhecidas
              </h2>
              <p>O conteúdo a seguir indicado não é acessível pelos seguintes motivos:</p>
              <ul className="mt-3 ml-5 list-outside list-disc space-y-2">
                <li>
                  <strong className="text-on-surface font-medium">Documentos PDF anexos</strong>:
                  alguns documentos publicados anteriormente à criação deste sítio Web podem não
                  estar totalmente acessíveis (ausência de marcação semântica, texto em imagem).
                  Estamos a substituir progressivamente os documentos por versões acessíveis.
                </li>
                <li>
                  <strong className="text-on-surface font-medium">Mapa interativo</strong>: o mapa
                  interativo embebido na página de Contactos depende de um serviço externo cujo
                  conteúdo não controlamos. As moradas e contactos completos estão também
                  disponíveis em formato textual na mesma página.
                </li>
              </ul>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>4 — Mecanismos de contacto</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Comunicar um problema de acessibilidade
              </h2>
              <p>
                Caso encontre problemas de acessibilidade neste sítio Web, agradecemos que nos
                contacte para que possamos resolver a situação:
              </p>
              <ul className="mt-3 ml-5 list-outside list-disc space-y-2">
                {site.contactEmail ? (
                  <li>
                    Correio eletrónico:{' '}
                    <a
                      href={`mailto:${site.contactEmail}`}
                      className="text-primary font-medium hover:underline"
                    >
                      {site.contactEmail}
                    </a>
                  </li>
                ) : null}
                {hq?.phone ? (
                  <li>
                    Telefone: <span className="text-on-surface font-medium">{hq.phone}</span>
                  </li>
                ) : null}
                <li>
                  Presencialmente, na nossa sede{hq?.address ? `, ${hq.address}` : ''}, dentro do
                  horário de atendimento.
                </li>
              </ul>
              <p className="mt-3">
                Procuraremos responder aos pedidos de acessibilidade no prazo legal aplicável ou,
                quando tal não seja possível, informar sobre o encaminhamento do pedido.
              </p>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>5 — Procedimento de execução</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Apresentar reclamação
              </h2>
              <p>
                Se considerar que o pedido de feedback não foi tratado de forma satisfatória, pode
                apresentar reclamação junto da Agência para a Modernização Administrativa, I.P.
                (AMA), entidade responsável pela monitorização da acessibilidade dos sítios web do
                setor público em Portugal:{' '}
                <a
                  href="https://www.acessibilidade.gov.pt"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackUmamiEvent('external_link_click', {
                      target: 'https://www.acessibilidade.gov.pt',
                      label: 'Acessibilidade.gov.pt',
                      source: 'accessibility',
                    })
                  }
                  className="text-primary font-medium hover:underline"
                >
                  www.acessibilidade.gov.pt
                </a>
                .
              </p>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>6 — Métodos de avaliação</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Como avaliámos a conformidade
              </h2>
              <p>
                A conformidade desta declaração com o Decreto-Lei n.º 83/2018 foi verificada com
                recurso a:
              </p>
              <ul className="mt-3 ml-5 list-outside list-disc space-y-2">
                <li>
                  Autoavaliação efetuada pela equipa responsável pelo desenvolvimento do sítio Web.
                </li>
                <li>
                  Verificação manual da navegação por teclado, contraste e estrutura semântica.
                </li>
                <li>
                  Validação automática através de ferramentas de análise de código e acessibilidade.
                </li>
              </ul>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>7 — Atualizações</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Revisão periódica
              </h2>
              <p>
                Esta declaração é revista anualmente ou sempre que sejam efetuadas alterações
                relevantes ao sítio Web. A versão em vigor encontra-se sempre disponível nesta
                página.
              </p>
            </section>
          </article>
        </div>
      </div>
    </>
  );
}
